<template>
  <div class="selection-item">
    <h3>{{bone.name}} {{$t('editor.bone.properties')}}</h3>
    <n-input-text
      :label="$t('editor.bone.name')"
      :value="bone.name"
      @change="handleNameChange" />
    <div class="group">
      <n-input-number
        :disabled="bone.parent > -1"
        :label="$t('editor.bone.start.x')"
        :value="bone.start[0].toFixed(2)"
        @change="handleStartXChange" />
      <n-input-number
        :disabled="bone.parent > -1"
        :label="$t('editor.bone.start.y')"
        :value="bone.start[1].toFixed(2)"
        @change="handleStartYChange" />
    </div>
    <div class="group">
      <n-input-number
        :label="$t('editor.bone.end.x')"
        :value="bone.end[0].toFixed(2)"
        @change="handleEndXChange" />
      <n-input-number
        :label="$t('editor.bone.end.y')"
        :value="bone.end[1].toFixed(2)"
        @change="handleEndYChange" />
    </div>
    <n-input-select
      :label="$t('editor.bone.parent')"
      :options="mapBonesToOptions(cropData.bones, index, true)"
      :value="bone.parent"
      @change="handleParentChange" />
    <div class="bbch group">
      <n-input-number
        :label="$t('editor.bone.bbch.min')"
        min="0"
        :max="bone.BBCH[1]"
        :value="bone.BBCH[0]"
        @change="handleMinBBCHChange" />
      <n-input-number
        :label="$t('editor.bone.bbch.max')"
        :min="bone.BBCH[0]"
        max="99"
        :value="bone.BBCH[1]"
        @change="handleMaxBBCHChange" />
    </div>
    <div class="rendering">
      <h3>Rendering options</h3>
      <n-input-switch
        :label="$t('editor.bone.flag.flip-x')"
        :checked="hasFlag(bone.flags, 0x01)"
        @change="handleFlagChange($event, 0x01)" />
      <n-input-switch
        :label="$t('editor.bone.flag.flip-y')"
        :checked="hasFlag(bone.flags, 0x02)"
        @change="handleFlagChange($event, 0x02)" />
      <n-input-switch
        :label="$t('editor.bone.flag.flip-orientation')"
        :checked="hasFlag(bone.flags, 0x04)"
        @change="handleFlagChange($event, 0x04)" />
      <n-input-switch
        :label="$t('editor.bone.flag.width-independent')"
        :checked="hasFlag(bone.flags, 0x08)"
        @change="handleFlagChange($event, 0x08)" />
      <n-input-switch
        :label="$t('editor.bone.flag.length-independent')"
        :checked="hasFlag(bone.flags, 0x10)"
        @change="handleFlagChange($event, 0x10)" />
      <n-input-switch
        :label="$t('editor.bone.flag.scale')"
        :checked="hasFlag(bone.flags, 0x20)"
        @change="handleFlagChange($event, 0x20)" />
    </div>
    <div class="images">
      <h3>{{$t('editor.bone.images')}}</h3>
      <div v-for="(boneImage, boneImageIndex) in bone.image.keys" :key="`bone-image-${boneImageIndex}`" class="image group">
        <n-input-number
          :label="$t('editor.bone.image.bbch')"
          :value="boneImage[0]"
          min="0"
          max="99"
          @change="handleImageBBCHChange($event, boneImageIndex)" />
        <n-input-select
          :label="$t('editor.bone.image.name')"
          :options="mapImagesToOptions(cropData.images)"
          :value="boneImage[1].value"
          @change="handleImageNameChange($event, boneImageIndex)" />
        <button v-if="boneImageIndex > 0" @click="handleImageRemoveClick($event, boneImageIndex)" type="button" class="button--remove-image">
          <n-icon-base>
            <n-icon-delete />
          </n-icon-base>
        </button>
      </div>
    </div>
    <button type="button" @click="handleImageAddClick">
      {{$t('editor.bone.add-image-key')}}
    </button>
    <h3>{{$t('editor.bone.danger-zone')}}</h3>
    <button type="button" class="button--remove" @click="handleRemoveClick">
      {{$t('editor.bone.remove')}}
    </button>
  </div>
</template>

<style lang="scss" scoped>

h3 {
  margin-top: 3rem;
}

.info__label {
  @extend %text-style--label;
}

.button--remove {
  background: var(--color--negative);
}

.button--remove-image {
  margin-left: 2rem;
}

.group {
  display: flex;
}

</style>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'

import IconBase from '@/components/icons/IconBase'
import IconDelete from '@/components/icons/IconDelete'
import InputText from '@/components/InputText'
import InputSwitch from '@/components/InputSwitch'
import InputNumber from '@/components/InputNumber'
import InputSelect from '@/components/InputSelect'

import map from '@/helpers/map'
import { hasFlag } from '@/helpers/flags'
import { hasCyclicDependencies } from '@/helpers/bone'

export default {
  components: {
    'n-icon-base': IconBase,
    'n-icon-delete': IconDelete,
    'n-input-text': InputText,
    'n-input-number': InputNumber,
    'n-input-switch': InputSwitch,
    'n-input-select': InputSelect
  },
  props: {
    index: {
      type: Number
    }
  },
  computed: {
    ...mapState({
      cropData: (state) => state.crop.data
    }),
    bones () {
      return this.cropData.bones
    },
    bone () {
      return this.cropData.bones[this.index]
    }
  },
  methods: {
    ...mapMutations({
      setBoneName: 'crop/setBoneName',
      setBoneStartX: 'crop/setBoneStartX',
      setBoneStartY: 'crop/setBoneStartY',
      setBoneEndX: 'crop/setBoneEndX',
      setBoneEndY: 'crop/setBoneEndY',
      setBoneParent: 'crop/setBoneParent',
      setBoneFlag: 'crop/setBoneFlag',
      setBoneBBCH: 'crop/setBoneBBCH',
      setBoneImageBBCH: 'crop/setBoneImageBBCH',
      setBoneImageName: 'crop/setBoneImageName',
      addBoneImage: 'crop/addBoneImage',
      removeBoneImage: 'crop/removeBoneImage'
    }),
    ...mapActions({
      removeBoneAt: 'editor/removeBoneAt'
    }),
    mapBoneToOption (bone, bones) {
      return bones.findIndex((current) => bone === current)
    },
    mapBonesToOptions (bones, boneIndex, hasEmpty = false) {
      if (hasEmpty) {
        return [{
          key: 'none',
          text: 'None',
          value: -1
        }].concat(
          bones
            .map((bone, index) => ({
              key: bone.id,
              text: bone.name,
              value: index
            }))
            .filter((bone, index) => index !== boneIndex)
        )
      }
      return bones
        .map((bone, index) => ({
          key: bone.id,
          text: bone.name,
          value: index
        }))
        .filter((bone, index) => index !== boneIndex)
    },
    mapImageToOption (image) {
      return image.name
    },
    mapImagesToOptions (images) {
      return map.keysToArray(images).map((image) => ({
        text: image,
        value: image
      }))
    },
    hasFlag (flags, flag) {
      return hasFlag(flags, flag)
    },
    handleNameChange (e) {
      this.setBoneName({ index: this.index, name: e.target.value })
    },
    handleStartXChange (e) {
      this.setBoneStartX({ index: this.index, x: e.target.valueAsNumber })
    },
    handleStartYChange (e) {
      this.setBoneStartY({ index: this.index, y: e.target.valueAsNumber })
    },
    handleEndXChange (e) {
      this.setBoneEndX({ index: this.index, x: e.target.valueAsNumber })
    },
    handleEndYChange (e) {
      this.setBoneEndY({ index: this.index, y: e.target.valueAsNumber })
    },
    handleParentChange (e) {
      const newParent = parseInt(e.target.selectedOptions[0].value, 10)
      const boneToTest = {
        ...this.bone,
        parent: newParent
      }
      if (hasCyclicDependencies(boneToTest, this.index, this.bones)) {
        alert('Bone cyclic dependency detected')
      } else {
        this.setBoneParent({
          index: this.index,
          parent: newParent
        })
      }
    },
    handleMinBBCHChange (e) {
      this.setBoneBBCH({ index: this.index, min: e.target.valueAsNumber })
    },
    handleMaxBBCHChange (e) {
      this.setBoneBBCH({ index: this.index, max: e.target.valueAsNumber })
    },
    handleFlagChange (e, flag) {
      this.setBoneFlag({ index: this.index, flag, checked: e.target.checked })
    },
    handleImageBBCHChange (e, imageIndex) {
      this.setBoneImageBBCH({ index: this.index, imageIndex, BBCH: e.target.valueAsNumber })
    },
    handleImageNameChange (e, imageIndex) {
      this.setBoneImageName({ index: this.index, imageIndex, value: e.target.selectedOptions[0].value })
    },
    handleImageAddClick (e) {
      this.addBoneImage({ index: this.index })
    },
    handleImageRemoveClick (e, imageIndex) {
      this.removeBoneImage({ index: this.index, imageIndex })
    },
    handleRemoveClick (e) {
      if (confirm(this.$t('editor.bone.remove-warning'))) {
        this.removeBoneAt({ index: this.index })
      }
    }
  }
}
</script>
